/**
 * What picking a customer tells the sales-source dialog about how the account buys.
 *
 * The customer list (`/customers`) carries each account's `customer_type` and, for a retailer
 * that buys through a distributor, its `parent_customer_id`. 1st Phorm's accounts came in from
 * Vividly typed `Direct`, `Direct Distributor` or `Distributor`, with every retailer under its
 * distributor as the parent. The dialog used to make the planner choose "Consumption" vs
 * "Ship-to" by hand and type the distributor split, which is how Kroger (a direct account) was
 * offered a route to market of DSD and UNFI could be split under UNFI.
 */

export interface CustomerKindRecord {
  id: string
  name: string
  customer_type?: string | null
  parent_customer_id?: string | null
}

export type CustomerKind = "consumption" | "ship_to"

export interface CustomerKindHint {
  kind: CustomerKind
  /** The distributor the account buys through, when its record names one (its parent). */
  distributor?: string
  /** Route to market read from the type; unset when the record says nothing. */
  route_to_market?: string
  /** One line for the dialog saying why it chose what it chose. */
  reason?: string
}

/** True when the account is one the brand invoices directly (a distributor or a direct chain). */
export function customer_type_is_ship_to(customer_type: string | null | undefined): boolean {
  const type = (customer_type ?? "").trim().toLowerCase()
  if (!type) return false
  return type.includes("distributor") || type.includes("direct")
}

/** The record for a typed or picked name, matched case-insensitively. */
export function find_customer_record(customers: CustomerKindRecord[], name: string): CustomerKindRecord | undefined {
  const key = name.trim().toLowerCase()
  if (!key) return undefined
  return customers.find((customer: CustomerKindRecord) => customer.name.trim().toLowerCase() === key)
}

/**
 * The kind, distributor and route to market a customer's record implies. `undefined` when the
 * name is not a known account (a free-typed customer), so the dialog leaves its own choices alone.
 */
export function customer_kind_hint(customers: CustomerKindRecord[], name: string): CustomerKindHint | undefined {
  const record = find_customer_record(customers, name)
  if (!record) return undefined
  const type = (record.customer_type ?? "").trim()
  if (customer_type_is_ship_to(type)) {
    const is_distributor = type.toLowerCase().includes("distributor")
    return {
      kind: "ship_to",
      route_to_market: is_distributor ? "Indirect via Distributor" : "Direct to Retailer",
      reason: `${record.name} is a ${type.toLowerCase()} account: it becomes its own ship-to root, no distributor split.`,
    }
  }
  const parent = record.parent_customer_id
    ? customers.find((customer: CustomerKindRecord) => customer.id === record.parent_customer_id)
    : undefined
  if (parent) {
    return {
      kind: "consumption",
      distributor: parent.name,
      route_to_market: "Indirect via Distributor",
      reason: `${record.name} buys through ${parent.name}, so the split is set to ${parent.name} at 100%. Change it if that is not right.`,
    }
  }
  return { kind: "consumption" }
}

/**
 * Whether the route-to-market field has anything to say for this customer. A direct account
 * (Kroger) is its own route, so the field is hidden; a retailer under a distributor, a
 * distributor itself, or an account the record says nothing about keeps it.
 */
export function customer_routes_through_distributor(hint: CustomerKindHint | null | undefined): boolean {
  if (!hint) return true
  return hint.route_to_market !== "Direct to Retailer"
}

/** True for a distributor account (UNFI, KeHE): the brand invoices the distributor, not stores. */
export function is_distributor_account(hint: CustomerKindHint | null | undefined): boolean {
  return hint?.kind === "ship_to" && hint.route_to_market === "Indirect via Distributor"
}

/**
 * How the growth inputs read for a customer. Door counts and same-store sales are a retailer's
 * levers; a distributor's volume is the sum of the accounts it serves, so it gets one
 * "Account growth" rate (carried in `sameStoreSalesMultiplier`) and no store-count growth — the
 * generator still receives both numbers, with the store one at 0.
 */
export function growth_inputs_for_customer(hint: CustomerKindHint | null | undefined): "per_store" | "account" {
  return is_distributor_account(hint) ? "account" : "per_store"
}

/** One distributor's share of a customer's volume, as the dialog's split rows carry it. */
export interface DistributorSplitInput {
  name: string
  allocation_pct: number
}

/**
 * Why a distributor split cannot be saved: the same distributor twice, or shares adding to more
 * than 100%. Blank-name rows are ignored (they are what "Add distributor" starts with). `null`
 * when the split is fine — a total under 100% is allowed (the remainder is unassigned).
 */
export function distributor_split_error(rows: DistributorSplitInput[] | undefined | null): string | null {
  const named = (rows ?? []).filter((row: DistributorSplitInput) => row.name.trim())
  const seen = new Set<string>()
  for (const row of named) {
    const key = row.name.trim().toLowerCase()
    if (seen.has(key)) return `${row.name.trim()} is listed twice in the distributor split.`
    seen.add(key)
  }
  const total = named.reduce((sum: number, row: DistributorSplitInput) => sum + (Number(row.allocation_pct) || 0), 0)
  if (total > 100.5) return `Distributor split adds to ${Math.round(total)}% — it cannot exceed 100%.`
  return null
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  const unfi: CustomerKindRecord = { id: "u", name: "UNFI", customer_type: "Distributor" }
  const kroger: CustomerKindRecord = { id: "k", name: "Kroger", customer_type: "Direct" }
  const awg: CustomerKindRecord = { id: "a", name: "Associated Wholesale Grocers", customer_type: "Direct Distributor" }
  const whole_foods: CustomerKindRecord = { id: "w", name: "Whole Foods", parent_customer_id: "u" }
  const seven: CustomerKindRecord = { id: "7", name: "7-Eleven", customer_type: null, parent_customer_id: null }
  const customers = [unfi, kroger, awg, whole_foods, seven]

  describe("customer_type_is_ship_to", () => {
    it("reads distributors and direct accounts as ship-to, anything else as consumption", () => {
      expect(customer_type_is_ship_to("Distributor")).toBe(true)
      expect(customer_type_is_ship_to("Direct Distributor")).toBe(true)
      expect(customer_type_is_ship_to("direct")).toBe(true)
      expect(customer_type_is_ship_to("")).toBe(false)
      expect(customer_type_is_ship_to(null)).toBe(false)
      expect(customer_type_is_ship_to("Retailer")).toBe(false)
    })
  })

  describe("customer_kind_hint", () => {
    it("makes a distributor or direct account a ship-to root with no split", () => {
      expect(customer_kind_hint(customers, "unfi")?.kind).toBe("ship_to")
      expect(customer_kind_hint(customers, "UNFI")?.route_to_market).toBe("Indirect via Distributor")
      expect(customer_kind_hint(customers, "Kroger")).toMatchObject({ kind: "ship_to", route_to_market: "Direct to Retailer" })
      expect(customer_kind_hint(customers, "Kroger")?.distributor).toBeUndefined()
      expect(customer_kind_hint(customers, "Associated Wholesale Grocers")?.kind).toBe("ship_to")
    })

    it("sets a retailer's split to the distributor it buys through", () => {
      expect(customer_kind_hint(customers, "Whole Foods")).toMatchObject({
        kind: "consumption",
        distributor: "UNFI",
        route_to_market: "Indirect via Distributor",
      })
    })

    it("leaves an untyped, parentless account as a plain consumption customer", () => {
      expect(customer_kind_hint(customers, "7-Eleven")).toEqual({ kind: "consumption" })
    })

    it("says nothing about a name that is not an account", () => {
      expect(customer_kind_hint(customers, "Brand new chain")).toBeUndefined()
      expect(customer_kind_hint(customers, "")).toBeUndefined()
    })
  })

  describe("customer_routes_through_distributor", () => {
    it("hides the route for a direct account and keeps it for everyone else", () => {
      expect(customer_routes_through_distributor(customer_kind_hint(customers, "Kroger"))).toBe(false)
      expect(customer_routes_through_distributor(customer_kind_hint(customers, "Whole Foods"))).toBe(true)
      expect(customer_routes_through_distributor(customer_kind_hint(customers, "UNFI"))).toBe(true)
      expect(customer_routes_through_distributor(customer_kind_hint(customers, "7-Eleven"))).toBe(true)
      expect(customer_routes_through_distributor(undefined)).toBe(true)
    })
  })

  describe("growth_inputs_for_customer", () => {
    it("gives a distributor one account-growth rate and a retailer the per-store levers", () => {
      expect(is_distributor_account(customer_kind_hint(customers, "KeHE"))).toBe(false)
      expect(growth_inputs_for_customer(customer_kind_hint(customers, "UNFI"))).toBe("account")
      expect(growth_inputs_for_customer(customer_kind_hint(customers, "Associated Wholesale Grocers"))).toBe("account")
      expect(growth_inputs_for_customer(customer_kind_hint(customers, "Kroger"))).toBe("per_store")
      expect(growth_inputs_for_customer(customer_kind_hint(customers, "Whole Foods"))).toBe("per_store")
      expect(growth_inputs_for_customer(null)).toBe("per_store")
    })
  })

  describe("distributor_split_error", () => {
    it("accepts a split at or under 100% and ignores blank rows", () => {
      expect(distributor_split_error([{ name: "UNFI", allocation_pct: 60 }, { name: "KeHE", allocation_pct: 40 }])).toBeNull()
      expect(distributor_split_error([{ name: "UNFI", allocation_pct: 70 }, { name: "", allocation_pct: 30 }])).toBeNull()
      expect(distributor_split_error([])).toBeNull()
      expect(distributor_split_error(undefined)).toBeNull()
    })

    it("refuses more than 100%", () => {
      expect(distributor_split_error([{ name: "UNFI", allocation_pct: 60 }, { name: "KeHE", allocation_pct: 60 }])).toContain("120%")
    })

    it("refuses the same distributor twice, whatever the casing", () => {
      expect(distributor_split_error([{ name: "UNFI", allocation_pct: 50 }, { name: "unfi ", allocation_pct: 50 }])).toContain("twice")
    })
  })
}
